<template>
  <div :class="{ 'cfe--compact': compact }" class="cfe soft-inputs">
    <div v-for="group in visibleGroups" :key="group.key" :class="compact ? 'cfe__cell' : 'cfe__row'">
      <template v-for="(field, index) in group.fields">
        <div v-if="index === 0 || isShown(field.key)" :key="field.key" class="cfe__field">
          <div class="cfe__label-row">
            <span class="acq-label cfe__label">
              <v-icon class="cfe__icon" x-small>{{ field.icon }}</v-icon>{{ field.label }}<span v-if="isRequired(field.key)" class="cfe__req"> *</span>
            </span>
            <!-- Compact layout: note / second field are revealed by small actions in the label row -->
            <span v-if="compact" class="cfe__actions">
              <button v-if="field.note && !hideNotes && !isNoteShown(field.key)" :disabled="disabled" :title="`Notiz zu ${field.label} hinzufügen`"
                      class="add-link cfe__action" type="button" @click="openNote(field.key)">
                <v-icon x-small>mdi-plus</v-icon>Notiz
              </button>
              <button v-if="index === 0 && secondField(group) && !isShown(secondField(group).key)" :disabled="disabled"
                      :title="`${secondField(group).label} hinzufügen`" class="add-link cfe__action" type="button"
                      @click="expand(secondField(group).key)">
                <v-icon x-small>mdi-plus</v-icon>{{ secondField(group).short }}
              </button>
            </span>
          </div>
          <!-- Keep the wrapper: v-mask must only see the input of this text field -->
          <div v-if="field.kind === 'phone'" class="cfe__input">
            <v-text-field v-mask="InputMasks.phoneNumber(value[field.key])" :disabled="disabled" :placeholder="field.placeholder"
                          :rules="[RuleFactory.isValidPhoneNumber(), isRequired(field.key) ? RuleFactory.required() : true]"
                          :value="value[field.key]" color="info" dense hide-details="auto" outlined type="tel" validate-on-blur
                          @input="update(field.key, $event)"/>
          </div>
          <div v-else class="cfe__input">
            <v-text-field :append-icon="isValidMail(field) ? 'mdi-check-circle' : undefined" :disabled="disabled"
                          :hint="field.kind === 'url' && validateWebsite ? undefined : field.hint"
                          :placeholder="field.placeholder" :rules="fieldRules(field)" :type="field.kind === 'mail' ? 'email' : 'url'"
                          :value="value[field.key]" class="cfe__text" color="info" dense hide-details="auto" outlined validate-on-blur
                          @input="update(field.key, $event)"/>
          </div>
          <span v-if="phonePreview(field)" class="acq-tiny cfe__preview">
            <v-icon class="cfe__preview-icon" x-small>mdi-earth</v-icon>Erkannt als {{ phonePreview(field) }}
          </span>
          <template v-if="field.note && !hideNotes">
            <v-text-field v-if="isNoteShown(field.key)" :disabled="disabled" :value="value.notes[field.key]" class="cfe__note" color="info"
                          dense hide-details outlined placeholder="Notiz, z. B. nur vormittags erreichbar"
                          prepend-inner-icon="mdi-note-text-outline" @input="updateNote(field.key, $event)"/>
            <button v-else-if="!compact" :disabled="disabled" class="add-link" type="button" @click="openNote(field.key)">
              <v-icon x-small>mdi-plus</v-icon>Notiz
            </button>
          </template>
        </div>
        <div v-else-if="!compact" :key="`${field.key}-add`" class="cfe__field cfe__field--add">
          <button :disabled="disabled" class="add-link" type="button" @click="expand(field.key)">
            <v-icon x-small>mdi-plus</v-icon>{{ field.label }}
          </button>
        </div>
      </template>
    </div>
    <button v-if="extrasToggleVisible" :disabled="disabled" class="add-link cfe__extras" type="button" @click="showExtras = !showExtras">
      <v-icon x-small>{{ showExtras ? 'mdi-chevron-up' : 'mdi-plus' }}</v-icon>
      {{ showExtras ? 'Weitere Kontaktfelder ausblenden' : 'Weitere Kontaktfelder (Telefax, Website)' }}
    </button>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import {RuleFactory} from '@/helpers/ruleFactory.helper';
import {InputMasks} from '@/helpers/vueTheMask';
import {ContactFormValue, ContactNoteKey, ContactValueKey, toInternationalPhone, websiteRule} from '@/helpers/contactInformationForm.helper';

type FieldKind = 'mail' | 'phone' | 'url';
type ContactField = {
  key: ContactValueKey; label: string; icon: string; kind: FieldKind; note?: boolean; placeholder?: string; hint?: string;
  /** Short action text of a second field in the compact layout */
  short?: string;
};
type ContactGroup = { key: string; business?: boolean; fields: ContactField[] };

const GROUPS: ContactGroup[] = [
  {
    key: 'mail', fields: [
      {key: 'mail1', label: 'E-Mail', icon: 'mdi-email-outline', kind: 'mail', note: true, placeholder: 'name@schule.de'},
      {key: 'mail2', label: 'Zweite E-Mail', short: '2. E-Mail', icon: 'mdi-email-outline', kind: 'mail', note: true, placeholder: 'name@schule.de'},
    ],
  },
  {
    key: 'phone', fields: [
      {key: 'phone1', label: 'Festnetz', icon: 'mdi-phone-outline', kind: 'phone', note: true, placeholder: '030 1234567'},
      {key: 'phone2', label: 'Zweite Festnetznummer', short: '2. Nummer', icon: 'mdi-phone-outline', kind: 'phone', note: true, placeholder: '030 1234567'},
    ],
  },
  {
    key: 'mobile', fields: [
      {key: 'mobile1', label: 'Mobil', icon: 'mdi-cellphone', kind: 'phone', note: true, placeholder: '0170 1234567'},
      {key: 'mobile2', label: 'Zweite Mobilnummer', short: '2. Nummer', icon: 'mdi-cellphone', kind: 'phone', note: true, placeholder: '0170 1234567'},
    ],
  },
  {
    key: 'fax', business: true, fields: [
      {key: 'fax1', label: 'Telefax', icon: 'mdi-fax', kind: 'phone', placeholder: '030 1234568'},
      {key: 'fax2', label: 'Zweites Telefax', short: '2. Fax', icon: 'mdi-fax', kind: 'phone', placeholder: '030 1234568'},
    ],
  },
  {
    key: 'web', business: true, fields: [
      {key: 'website', label: 'Website', icon: 'mdi-web', kind: 'url', placeholder: 'https://muster.de', hint: 'Format: http://muster.de oder https://muster.de'},
    ],
  },
];

/** Order of the groups in the compact 2-column layout: E-Mail | Mobil, Festnetz | Telefax, Website */
const COMPACT_ORDER = ['mail', 'mobile', 'phone', 'fax', 'web'];

/**
 * Modern editor for a contact information (mail, phone, mobile, fax, website) with a note per field.
 * v-model: ContactFormValue (plain strings, see contactInformationForm.helper.ts). Second fields and notes are revealed with add links.
 * layout="compact": channels in a 2-column grid, add actions in the label row (default: one row per channel, as before).
 */
export default Vue.extend({
  props: {
    value: {type: Object as () => ContactFormValue, required: true},
    isBusinessContact: {type: Boolean, default: false},
    hideNotes: {type: Boolean, default: false},
    requireMail: {type: Boolean, default: false},
    requireMobile: {type: Boolean, default: false},
    disabled: {type: Boolean, default: false},
    /** 'default' (one row per channel) or 'compact' (2-column channel grid) */
    layout: {type: String as () => 'default' | 'compact', default: 'default'},
    /** Business fields (Telefax, Website) are hidden behind a "Weitere Kontaktfelder" link until opened or filled */
    collapseExtras: {type: Boolean, default: false},
    /** Inline validation of the website (same format the server accepts) instead of the format hint */
    validateWebsite: {type: Boolean, default: false},
  },
  data: () => ({
    expanded: [] as ContactValueKey[],
    openNotes: [] as ContactNoteKey[],
    showExtras: false,
  }),
  computed: {
    RuleFactory: () => RuleFactory,
    InputMasks: () => InputMasks,
    compact(): boolean {
      return this.layout === 'compact';
    },
    hasExtraValues(): boolean {
      return GROUPS.some((group) => group.business && group.fields.some((field) => !!this.value[field.key]));
    },
    extrasToggleVisible(): boolean {
      return this.collapseExtras && this.isBusinessContact && !this.hasExtraValues;
    },
    visibleGroups(): ContactGroup[] {
      const extrasHidden = this.collapseExtras && !this.showExtras && !this.hasExtraValues;
      const groups = GROUPS.filter((group) => !group.business || (this.isBusinessContact && !extrasHidden));
      if (!this.compact) {
        return groups;
      }
      return [...groups].sort((a, b) => COMPACT_ORDER.indexOf(a.key) - COMPACT_ORDER.indexOf(b.key));
    },
  },
  methods: {
    secondField(group: ContactGroup): ContactField | undefined {
      return group.fields[1];
    },
    isShown(key: ContactValueKey): boolean {
      return !!this.value[key] || this.expanded.includes(key);
    },
    isNoteShown(key: ContactValueKey): boolean {
      const noteKey = key as ContactNoteKey;
      return !!this.value.notes[noteKey] || this.openNotes.includes(noteKey);
    },
    isRequired(key: ContactValueKey): boolean {
      return (key === 'mail1' && this.requireMail) || (key === 'mobile1' && this.requireMobile);
    },
    fieldRules(field: ContactField) {
      if (field.kind === 'url') {
        return this.validateWebsite ? [websiteRule] : [];
      }
      if (field.kind !== 'mail') {
        return [];
      }
      return [RuleFactory.isValidMail(), this.isRequired(field.key) ? RuleFactory.required() : true];
    },
    isValidMail(field: ContactField): boolean {
      const v = this.value[field.key];
      return field.kind === 'mail' && !!v && RuleFactory.isValidMail()(v) === true;
    },
    phonePreview(field: ContactField): string {
      const v = this.value[field.key];
      if (field.kind !== 'phone' || !v || RuleFactory.isValidPhoneNumber()(v) !== true) {
        return '';
      }
      const international = toInternationalPhone(v);
      return international && international !== v ? international : '';
    },
    expand(key: ContactValueKey) {
      this.expanded.push(key);
    },
    openNote(key: ContactValueKey) {
      this.openNotes.push(key as ContactNoteKey);
    },
    update(key: ContactValueKey, v: string | null) {
      this.$emit('input', {...this.value, [key]: v || ''});
    },
    updateNote(key: ContactValueKey, v: string | null) {
      this.$emit('input', {...this.value, notes: {...this.value.notes, [key]: v || ''}});
    },
  },
});
</script>

<style scoped>
.cfe {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.cfe__row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

@media (max-width: 599px) {
  .cfe__row {
    grid-template-columns: minmax(0, 1fr);
  }
}

/* Compact layout: one cell per channel, 2 columns */
.cfe--compact {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 16px;
}

@media (max-width: 599px) {
  .cfe--compact {
    grid-template-columns: minmax(0, 1fr);
  }
}

.cfe__cell {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}

.cfe__label-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  min-height: 18px;
}

.cfe__actions {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
  flex-shrink: 0;
}

.cfe__action {
  font-size: 12px !important;
  white-space: nowrap;
}

.cfe__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.cfe__field--add {
  justify-content: flex-end;
  padding-bottom: 10px;
}

.cfe__extras {
  align-self: flex-start;
}

.cfe--compact .cfe__extras {
  grid-column: 1 / -1;
}

.cfe__label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.cfe__icon, .cfe__preview-icon {
  color: inherit !important;
}

.cfe__req {
  color: #E5484D;
}

.cfe__preview {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.cfe__text >>> .mdi-check-circle {
  color: #2E7D32 !important;
}

.cfe__note >>> .v-input__slot {
  min-height: 34px !important;
  font-size: 13px;
}
</style>
<style src="@/components/acquisitionList/ui/acquisitionViews.css"></style>
<style src="./modern.css"></style>
<style src="./modernDialog.css"></style>
